import { IFluidCodeDetails } from '@fluidframework/core-interfaces';
import { ComponentLoaderData, NotificationService, ShareService } from '@fluidx/office-fluid-types';
import { createOdspCreateContainerRequest } from '@fluidframework/odsp-driver/lib/createOdspCreateContainerRequest';
import {
    CreateNewTracker,
    DriverInfo,
    LoadInIframeResult,
    OfficeWebHostScope,
} from '@ms/office-web-host';
import { PerformanceDatapoint } from 'owa-analytics';
import { getSessionId } from 'owa-config';
import { timestamp } from 'owa-datetime';
import { fluidComponentError, fluidComponentLoaded } from 'owa-fluid-datapoints';
import { reportPerfCheckmark, OWAFluidCheckmarksEnum } from 'owa-fluid-perf-datapoints';
import {
    FluidLoadOwaScenario,
    FluidOwaSource,
    FluidLoadSource,
    FluidOwaScenario,
} from 'owa-fluid-validations';
import { onFluidFileCreated, onFluidFileCreationFailed } from 'owa-fluid-link';
import * as trace from 'owa-trace';
import ReactDOM from 'react-dom';
import { Deferred } from './Deferred';
import { getDriverDetailsForFluidItem } from './getDriverDetailsForFluidItem';
import { createFluidTargetAndHeader } from './createFluidTargetAndHeader';
import { renderFluidComponent } from '../../components/FluidComponent';
import { getFluidPropsForRender } from '../fluidProps/getFluidPropsForRender';
import {
    getNotificationServicePromise,
    getShareServicePromise,
} from '../fluidProps/hostContext/scopeHelpers';
import {
    isFluidContainerConfigForEditor,
    isFluidContainerConfigForDiv,
    FluidContainerConfig,
} from '../../store/schema/FluidContainerConfig';
import { getScenarioNameFromScenarioAndSource } from '../getScenarioNameFromScenarioAndSource';

// Creates and mounts a new fluid component in an editor. Handles insertion and clean up.
export async function mountNewFluidComponent(
    containerConfig: FluidContainerConfig,
    originalNode: Node,
    driverInfo: DriverInfo,
    codeDetails: IFluidCodeDetails,
    scenarioStartTime: number,
    owaSource: FluidOwaSource,
    scenario: FluidOwaScenario,
    addRecipientFunc: (address: string) => void,
    heroComponentConfiguration?: ComponentLoaderData | undefined,
    viewStateId?: string
) {
    const scenarioName: string = getScenarioNameFromScenarioAndSource(scenario, owaSource);
    const perfDatapoint = new PerformanceDatapoint('FluidMountNewComponent');
    perfDatapoint.addCustomData({
        differenceFromStartTime: timestamp() - scenarioStartTime,
        scenario: scenarioName,
    });

    const containerId = containerConfig.containerId;

    let editor, containerDiv;
    if (isFluidContainerConfigForDiv(containerConfig)) {
        containerDiv = containerConfig.containerDiv;
    } else if (isFluidContainerConfigForEditor(containerConfig)) {
        editor = containerConfig.editor;
    }

    const [targetDiv, headerNode] = createFluidTargetAndHeader(
        editor,
        containerDiv,
        originalNode,
        containerId
    );

    // Get OWA's session Id from config
    const sessionId = getSessionId();
    const removeElementCallback = () => {
        ReactDOM.unmountComponentAtNode(targetDiv);
    };
    const mountComponentProps = getFluidPropsForRender(
        sessionId,
        scenarioName,
        scenarioStartTime,
        owaSource,
        codeDetails,
        undefined /* url */,
        removeElementCallback,
        containerConfig.customMenuItems
    );
    const notificationServicePromise = new Deferred<NotificationService>();
    const shareServicePromise = new Deferred<ShareService>();

    const scope: OfficeWebHostScope = {
        NotificationServicePromise: notificationServicePromise,
        ShareServicePromise: shareServicePromise,
        PresenceColorProvider: mountComponentProps.presenceColorProvider,
        StylingProvider: mountComponentProps.stylingProvider,
        HostContext: mountComponentProps.hostContext,
        TokenProvider: mountComponentProps.tokenProvider,
    };

    try {
        const result: LoadInIframeResult = await new Promise(resolve => {
            renderFluidComponent(
                mountComponentProps,
                scope,
                targetDiv,
                driverInfo,
                headerNode,
                resolve,
                owaSource,
                true, //isCreateNew
                containerConfig.controlNode,
                heroComponentConfiguration
            );
        });
        const loadedInfo = await result.loadedInfo;
        perfDatapoint.addCheckmark('fetchedLoadedInfo');
        containerConfig.onMount?.(loadedInfo);
        reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerBootStarted, owaSource);
        const [component, container] = await Promise.all([
            loadedInfo.component,
            loadedInfo.container,
        ]);
        perfDatapoint.addCheckmark('componentAndContainerResolved');
        const createNewTracker = new CreateNewTracker(mountComponentProps.hostConfig.logger);
        reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerResolved, owaSource);
        const newFileInfo = await driverInfo.newFileInfoPromise;
        const containerRequest = createOdspCreateContainerRequest(
            newFileInfo.siteUrl,
            newFileInfo.driveId,
            newFileInfo.filePath,
            newFileInfo.filename
        );

        let retryAttach = false;
        await container.attach(containerRequest).catch(error => {
            retryAttach = true;
        });
        if (retryAttach) {
            // attach can fail due to network or service interruptions, per fluid team guidance we retry once
            await container.attach(containerRequest).catch(error => {
                // We do not retry a third time, as success is unlikely after two failures.
                container.close();
                createNewTracker.setFailure(true /*onAttach*/, error);
                throw error;
            });
        }
        perfDatapoint.addCheckmark('attachedContainer');

        const loadable = component?.IFluidLoadable;
        const relativeUrl = loadable?.handle.absolutePath || '/';
        let url = await container.getAbsoluteUrl(relativeUrl).catch(error => {
            /* swallow the error and retry */
        });
        if (!url) {
            // getAbsoluteUrl can fail due to network or service interruptions, per fluid team guidance we retry once
            url = await container.getAbsoluteUrl(relativeUrl).catch(error => {
                container.close();
                createNewTracker.setFailure(false /*onAttach*/, error);
                throw error;
            });
        }

        // If the url is undefined, close the container and call the CreateNewTracker's setFailure method
        if (!url) {
            container.close();
            createNewTracker.setFailure(
                false /*onAttach*/,
                new Error('container.getAbsoluteUrl returned undefined')
            );
        }

        perfDatapoint.addCheckmark('retrieved url');
        createNewTracker.setSuccess();
        const resolvedDriverInfo = getDriverDetailsForFluidItem(url);
        perfDatapoint.addCheckmark('fetchedDriverDetails');
        reportPerfCheckmark(OWAFluidCheckmarksEnum.driverDetailsFetched, owaSource);

        if (resolvedDriverInfo) {
            notificationServicePromise.resolve(
                getNotificationServicePromise(
                    mountComponentProps.tokenProvider,
                    resolvedDriverInfo.fileId,
                    resolvedDriverInfo.driveId,
                    resolvedDriverInfo.siteUrl,
                    mountComponentProps.hostConfig.logger,
                    sessionId,
                    addRecipientFunc
                )
            );
            shareServicePromise.resolve(
                getShareServicePromise(
                    mountComponentProps.tokenProvider,
                    mountComponentProps.hostConfig.logger,
                    resolvedDriverInfo.fileId,
                    resolvedDriverInfo.driveId,
                    resolvedDriverInfo.siteUrl,
                    addRecipientFunc
                )
            );
        } else {
            const errorMessage = 'GetLocatorFromOdspUrl failed to return driverInfo object';
            const error = new Error(errorMessage);
            notificationServicePromise.reject(error);
            shareServicePromise.reject(error);
        }

        onFluidFileCreated(containerId, owaSource, url, viewStateId);

        fluidComponentLoaded(
            FluidLoadOwaScenario.ComposeWithNewComponent,
            owaSource,
            FluidLoadSource.Bohemia,
            scenarioStartTime
        );
        perfDatapoint.end();
    } catch (error) {
        onFluidFileCreationFailed(containerId, owaSource, viewStateId);
        if (editor && !editor.isDisposed()) {
            editor.deleteNode(targetDiv);
        }
        fluidComponentError(
            FluidLoadOwaScenario.ComposeWithNewComponent,
            owaSource,
            FluidLoadSource.Bohemia,
            error
        );
        trace.debugErrorThatWillShowErrorPopupOnly('New fluid component failed to load' + error);
        perfDatapoint.endWithError(error);
    }
}
